import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import NewAgentButton from '@/components/page-components/project/agents/ui/NewAgentButton';
import { MessagesSquare } from 'lucide-react';
import type { Agent } from '@/types/Agents';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectAgents,
  selectAgentsStatus,
  fetchAgents,
} from '@/redux/agentsSlice';
import {
  selectConversations,
  fetchConversations,
} from '@/redux/conversationsSlice';
import type { AppDispatch } from '@/redux/store';
import AgentsListItem from '@/components/page-components/project/agents/ui/AgentsListItem';
import AgentsListSkeleton from '@/components/page-components/project/agents/ui/AgentsListSkeleton';
import { navigateToAgent } from '@/utils/navigationUtils';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import ResponsiveGrid from '@/components/ui/responsive-grid';
import AgentSearch from '@/components/common/AgentSearch';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { useIsDesktop } from '@/hooks/use-mobile';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

const Home: React.FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const agents = useSelector(selectAgents);
  const agentsStatus = useSelector(selectAgentsStatus);
  const conversations = useSelector(selectConversations);
  const isDesktop = useIsDesktop();
  const isLoadingAgents =
    (agentsStatus === 'loading' || agentsStatus === 'idle') &&
    agents.length === 0;
  const [customOrder, setCustomOrder] = useState<number[]>([]);
  const [aggregatedStats, setAggregatedStats] = useState({
    totalCalls: 0,
    avgDuration: 0,
    totalDuration: 0,
    conversationsWithDuration: 0,
    totalMessages: 0,
    avgMessagesPerCall: 0,
    totalContacts: 0,
  });

  // Sensors for drag and drop (desktop only — touch devices use tap-to-open)
  const dragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const noDragSensors = useSensors();

  // Load custom order from localStorage on component mount
  useEffect(() => {
    const savedOrder = localStorage.getItem('agentsCustomOrder');
    if (savedOrder) {
      try {
        const parsed = JSON.parse(savedOrder);
        if (Array.isArray(parsed)) {
          setCustomOrder(parsed);
        }
      } catch (error) {
        console.error('Failed to parse custom order from localStorage:', error);
      }
    }
  }, []);

  // Sort agents based on custom order, with new agents at the end
  const orderedAgents = useMemo(() => {
    if (!agents || agents.length === 0) return [];

    // If no custom order exists, sort by oldest first
    if (customOrder.length === 0) {
      return [...agents].sort((a: Agent, b: Agent) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateA - dateB;
      });
    }

    // Create a map for quick lookup
    const agentMap = new Map(agents.map(agent => [agent.id, agent]));

    // Get agents in custom order
    const ordered: Agent[] = [];
    const orderedIds = new Set<number>();

    for (const id of customOrder) {
      const agent = agentMap.get(id);
      if (agent) {
        ordered.push(agent);
        orderedIds.add(id);
      }
    }

    // Add any new agents that aren't in the custom order (at the end)
    for (const agent of agents) {
      if (agent.id && !orderedIds.has(agent.id)) {
        ordered.push(agent);
      }
    }

    return ordered;
  }, [agents, customOrder]);

  useEffect(() => {
    if (agentsStatus === 'idle' && agents.length === 0) {
      dispatch(fetchAgents());
    }
  }, [dispatch, agentsStatus, agents.length]);

  useEffect(() => {
    if (agents && agents.length > 0) {
      dispatch(fetchConversations());
    }
  }, [agents, dispatch]);

  // Calculate aggregated stats from all agents
  useEffect(() => {
    if (agents && conversations) {
      try {
        const totalCalls = conversations.length;
        const conversationsWithDuration = conversations.filter(
          conv => conv.duration
        ).length;
        const totalDuration = conversations.reduce(
          (sum, conv) => sum + (conv.duration || 0),
          0
        );
        const avgDuration =
          conversationsWithDuration > 0
            ? totalDuration / conversationsWithDuration
            : 0;

        // Calculate additional metrics
        const totalMessages = conversations.reduce(
          (sum, conv) => sum + (conv.messages?.length || 0),
          0
        );
        const avgMessagesPerCall =
          totalCalls > 0 ? totalMessages / totalCalls : 0;

        // Calculate total contacts based on unique caller_id and agent_id combinations
        const uniqueContacts = new Set<string>();
        conversations.forEach(conv => {
          const contactKey = `${conv.agent_id}-${conv.caller_id}`;
          uniqueContacts.add(contactKey);
        });
        const totalContacts = uniqueContacts.size;

        setAggregatedStats({
          totalCalls,
          avgDuration,
          totalDuration,
          conversationsWithDuration,
          totalMessages,
          avgMessagesPerCall,
          totalContacts,
        });
      } catch (error) {
        console.error('Error calculating aggregated stats:', error);
      }
    }
  }, [agents, conversations]);

  useEffect(() => {
    if (location.state?.openCreateAgent) {
      navigate('/agents/new');
      // Optionally clear the state so it doesn't reopen on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, navigate]);

  useEffect(() => {
    // Refresh agents list when returning from agent creation
    if (location.pathname === '/home') {
      dispatch(fetchAgents());
    }
  }, [location.pathname, dispatch]);

  const handleCardClick = (agentId: number) => {
    navigateToAgent(navigate, agentId);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = orderedAgents.findIndex(agent => agent.id === active.id);
      const newIndex = orderedAgents.findIndex(agent => agent.id === over.id);

      const newOrderedAgents = arrayMove(orderedAgents, oldIndex, newIndex);
      const newOrder = newOrderedAgents
        .map(agent => agent.id)
        .filter((id): id is number => id !== undefined);

      setCustomOrder(newOrder);

      // Save to localStorage
      try {
        localStorage.setItem('agentsCustomOrder', JSON.stringify(newOrder));
      } catch (error) {
        console.error('Failed to save custom order to localStorage:', error);
      }
    }
  };

  return (
    <div className="flex flex-col justify-center items-center w-full gap-4 sm:gap-5">
      <div className="w-full flex flex-row sm:flex-row sm:justify-end gap-3 sm:gap-4 items-center">
        <NewAgentButton
          variant="gradient"
          className="flex-shrink-0 order-2 sm:order-1 h-12 lg:h-9 w-12 min-w-[3rem] sm:w-auto sm:min-w-0 sm:px-4"
        />
        <AgentSearch
          agents={agents || []}
          className="flex-1 min-w-0 order-1 sm:order-2 sm:flex-none"
        />
      </div>
      {isLoadingAgents ? (
        <div className="w-full space-y-4">
          <AgentsListSkeleton />
        </div>
      ) : orderedAgents && orderedAgents.length > 0 ? (
        <div className="flex flex-col items-center justify-center gap-6 sm:gap-8 w-full">
          {/* Performance Metrics Dashboard */}
          <div className="w-full">
            <ResponsiveGrid cols={{ sm: 1, md: 2, lg: 4 }}>
              <CardTitle>{t('dashboard.overview')}</CardTitle>
              <Card className="col-span-1 md:col-span-2 lg:col-span-4 bg-card">
                <CardContent className="px-3 pt-3 sm:px-6 sm:pt-4 md:pt-6">
                  <div className="grid grid-cols-4 gap-x-1 gap-y-1 sm:gap-x-4 sm:gap-y-2">
                    <p className="min-w-0 text-[10px] leading-snug text-muted-foreground sm:text-sm">
                      {t(
                        'agent.dashboard.overview.total_conversations.translation'
                      )}
                    </p>
                    <p className="min-w-0 text-[10px] leading-snug text-muted-foreground sm:text-sm">
                      {t('agent.dashboard.overview.total_contacts.translation')}
                    </p>
                    <p className="min-w-0 text-[10px] leading-snug text-muted-foreground sm:text-sm">
                      {t('agent.dashboard.overview.avg_duration.translation')}
                    </p>
                    <p className="min-w-0 text-[10px] leading-snug text-muted-foreground sm:text-sm">
                      {t('agent.dashboard.overview.total_messages.translation')}
                    </p>
                    <p className="text-base font-bold sm:text-xl lg:text-2xl">
                      {aggregatedStats?.totalCalls || 0}
                    </p>
                    <p className="text-base font-bold sm:text-xl lg:text-2xl">
                      {aggregatedStats?.totalContacts || 0}
                    </p>
                    <p className="text-base font-bold sm:text-xl lg:text-2xl">
                      {t(
                        'agent.dashboard.overview.avg_duration_value.translation',
                        {
                          value: Math.round(aggregatedStats?.avgDuration || 0),
                        }
                      )}
                    </p>
                    <p className="text-base font-bold sm:text-xl lg:text-2xl">
                      {aggregatedStats?.totalMessages || 0}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </ResponsiveGrid>
          </div>

          <div className="w-full space-y-4">
            <CardTitle>{t('dashboard.agents')}</CardTitle>
            <DndContext
              sensors={isDesktop ? dragSensors : noDragSensors}
              collisionDetection={closestCenter}
              onDragEnd={isDesktop ? handleDragEnd : undefined}
            >
              <SortableContext
                items={orderedAgents.map(agent => agent.id!)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {orderedAgents.map((agent: Agent) => (
                    <AgentsListItem
                      key={agent.id}
                      agent={agent}
                      onCardClick={handleCardClick}
                      enableDrag={isDesktop}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-4 sm:p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-card rounded-full flex items-center justify-center">
              <MessagesSquare className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
            </div>
            <div className="flex flex-col gap-3">
              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                {t('agent.list.empty.title.translation')}
              </h3>
              <p className="text-sm text-muted-foreground px-4 sm:px-0">
                {t('agent.list.empty.description.translation')}
              </p>
            </div>
            <NewAgentButton
              className="mt-4 w-full sm:w-auto"
              variant="transparent"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
